import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { clampSize, useDragResize } from "../FarmMap/useDragResize";
import { fetchMachineSchedule } from "../FieldApp/fieldAppApi";
import FieldAppMachineSchedulePanel from "../FieldApp/FieldAppMachineSchedulePanel";
import {
  PERIOD_PRESETS,
  buildCustomPeriod,
  buildLastDayPeriod,
  buildLastMonthPeriod,
  buildLastWeekPeriod,
} from "../FieldApp/fieldAppPeriodUtils";
import { readTrackingTimezoneMode } from "../FieldApp/fieldAppTimezoneDisplay";

import "./OperationFleetShiftsPanel.css";

const PANEL_STORAGE_PREFIX = "operation-fleet-shifts-panel-";
const LAST_DAY = "last_day";

const DEFAULT_LAYOUT = {
  x: 24,
  y: 40,
  width: 1080,
  height: 640,
};

function readLayout(farmId) {
  try {
    const raw = localStorage.getItem(`${PANEL_STORAGE_PREFIX}${farmId}`);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw);
    return {
      x: Number(parsed.x) || DEFAULT_LAYOUT.x,
      y: Number(parsed.y) || DEFAULT_LAYOUT.y,
      width: clampSize(Number(parsed.width) || DEFAULT_LAYOUT.width, 640, 1400),
      height: clampSize(Number(parsed.height) || DEFAULT_LAYOUT.height, 360, 960),
    };
  } catch (error) {
    return DEFAULT_LAYOUT;
  }
}

function writeLayout(farmId, layout) {
  try {
    localStorage.setItem(`${PANEL_STORAGE_PREFIX}${farmId}`, JSON.stringify(layout));
  } catch (error) {
    /* ignore */
  }
}

function sanitizeLayout(layout, bounds) {
  const width = clampSize(layout.width, 640, bounds ? bounds.width - 16 : 1400);
  const height = clampSize(layout.height, 360, bounds ? bounds.height - 16 : 960);
  const maxX = bounds ? Math.max(8, bounds.width - width - 8) : 9999;
  const maxY = bounds ? Math.max(8, bounds.height - height - 8) : 9999;
  return {
    x: clampSize(layout.x, 8, maxX),
    y: clampSize(layout.y, 8, maxY),
    width,
    height,
  };
}

function periodFromPreset(preset, customFrom, customTo) {
  if (preset === LAST_DAY) return buildLastDayPeriod();
  if (preset === PERIOD_PRESETS.LAST_WEEK) return buildLastWeekPeriod();
  if (preset === PERIOD_PRESETS.CUSTOM) {
    return buildCustomPeriod(customFrom, customTo);
  }
  return buildLastMonthPeriod();
}

function dayPart(value) {
  return String(value || "").slice(0, 10);
}

function formatDayLabel(value) {
  const day = dayPart(value);
  if (!day) return "";
  const [year, month, date] = day.split("-");
  if (!year || !month || !date) return day;
  return `${date}/${month}/${year}`;
}

function OperationFleetShiftsPanel({ farmId, machine, onClose, t }) {
  const [bounds, setBounds] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  }));
  const [layout, setLayout] = useState(() => readLayout(farmId));
  const [preset, setPreset] = useState(PERIOD_PRESETS.LAST_MONTH);
  const [customFrom, setCustomFrom] = useState(() => dayPart(buildLastMonthPeriod().dateFrom));
  const [customTo, setCustomTo] = useState(() => dayPart(buildLastMonthPeriod().dateTo));
  const [rows, setRows] = useState([]);
  const [meterType, setMeterType] = useState("hour_meter");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const period = useMemo(
    () => periodFromPreset(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  const selection = useMemo(
    () => ({
      operatorIds: [],
      equipmentIds: machine && machine.equipmentId ? [Number(machine.equipmentId)] : [],
      machineIdents: machine && machine.machineIdent ? [machine.machineIdent] : [],
      allMachines: false,
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      periodPreset: preset,
    }),
    [machine, period.dateFrom, period.dateTo, preset]
  );

  const timezoneMode = readTrackingTimezoneMode(farmId);
  const periodLabel = `${formatDayLabel(period.dateFrom)} — ${formatDayLabel(period.dateTo)}`;

  useEffect(() => {
    setLayout(readLayout(farmId));
  }, [farmId]);

  useEffect(() => {
    writeLayout(farmId, layout);
  }, [farmId, layout]);

  useEffect(() => {
    function updateBounds() {
      setBounds({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  useEffect(() => {
    if (!farmId || !machine || !machine.machineIdent) return undefined;

    let cancelled = false;
    async function loadRows() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchMachineSchedule(farmId, {
          machineIdent: machine.machineIdent,
          operatorIds: [],
          dateFrom: period.dateFrom,
          dateTo: period.dateTo,
        });
        if (cancelled) return;
        setRows(data.days || []);
        setMeterType(data.meterType || machine.meterType || "hour_meter");
      } catch (loadError) {
        console.error(loadError);
        if (cancelled) return;
        setRows([]);
        setError(t("Falha ao carregar planilha da máquina"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRows();
    return () => {
      cancelled = true;
    };
  }, [farmId, machine, period.dateFrom, period.dateTo, t]);

  useEffect(() => {
    if (!machine) return undefined;
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [machine, onClose]);

  const handlePanelResize = useCallback(
    (axis, dx, dy) => {
      if (axis === "move") {
        setLayout((current) =>
          sanitizeLayout(
            { ...current, x: current.x + dx, y: current.y + dy },
            bounds
          )
        );
        return;
      }
      if (axis === "resize") {
        setLayout((current) =>
          sanitizeLayout(
            {
              ...current,
              width: current.width + dx,
              height: current.height + dy,
            },
            bounds
          )
        );
      }
    },
    [bounds]
  );

  const { onResizeStart } = useDragResize(handlePanelResize);

  const handlePreset = useCallback((nextPreset) => {
    if (nextPreset === PERIOD_PRESETS.CUSTOM) {
      const current = periodFromPreset(preset, customFrom, customTo);
      setCustomFrom(dayPart(current.dateFrom));
      setCustomTo(dayPart(current.dateTo));
    }
    setPreset(nextPreset);
  }, [customFrom, customTo, preset]);

  if (!machine || !machine.machineIdent || typeof document === "undefined") {
    return null;
  }

  const title = t("Turnos — {{machine}}", {
    machine: machine.machineIdent,
  });

  return createPortal(
    <div className="operation-fleet-shifts-shell">
      <div
        className="operation-fleet-shifts-panel"
        style={{
          left: layout.x,
          top: layout.y,
          width: layout.width,
          height: layout.height,
        }}
      >
        <div className="operation-fleet-shifts-panel__header">
          <div
            className="operation-fleet-shifts-panel__drag"
            onMouseDown={(event) => onResizeStart("move", event)}
          >
            <span>{title}</span>
          </div>
          <button
            type="button"
            className="operation-fleet-shifts-panel__close"
            aria-label={t("Fechar planilha")}
            title={t("Fechar planilha")}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="operation-fleet-shifts-panel__toolbar">
          <div className="operation-fleet-shifts-panel__presets" role="tablist">
            {[
              { id: LAST_DAY, label: t("Último dia") },
              { id: PERIOD_PRESETS.LAST_WEEK, label: t("Última semana") },
              { id: PERIOD_PRESETS.LAST_MONTH, label: t("Último mês") },
              { id: PERIOD_PRESETS.CUSTOM, label: t("Personalizado") },
            ].map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={preset === item.id}
                className={
                  preset === item.id
                    ? "operation-fleet-shifts-panel__preset is-selected"
                    : "operation-fleet-shifts-panel__preset"
                }
                onClick={() => handlePreset(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {preset === PERIOD_PRESETS.CUSTOM ? (
            <div className="operation-fleet-shifts-panel__custom">
              <label>
                {t("De")}
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(event) => setCustomFrom(event.target.value)}
                />
              </label>
              <label>
                {t("Até")}
                <input
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(event) => setCustomTo(event.target.value)}
                />
              </label>
            </div>
          ) : (
            <p className="operation-fleet-shifts-panel__range">{periodLabel}</p>
          )}
        </div>
        <div className="operation-fleet-shifts-panel__body">
          <FieldAppMachineSchedulePanel
            hideToggle
            expanded
            onToggleExpanded={() => {}}
            rows={rows}
            meterType={meterType}
            loading={loading}
            error={error}
            timezoneMode={timezoneMode}
            farmId={farmId}
            selection={selection}
            machineLabel={machine.machineIdent}
            periodLabel={periodLabel}
            operatorLabel=""
            t={t}
          />
        </div>
        <div
          className="operation-fleet-shifts-panel__resize"
          title={t("Arraste para redimensionar")}
          onMouseDown={(event) => onResizeStart("resize", event)}
        />
      </div>
    </div>,
    document.body
  );
}

export default OperationFleetShiftsPanel;
